import React, { useState } from 'react';
import {
    Button,
    Typography,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Slide,
    Divider,
    Avatar,
    IconButton,
    Alert,
    TableRow,
    TableCell,
    Table,
    TableHead,
    Switch,
    FormControlLabel,
    TableBody,
    CircularProgress,
    TableContainer,
    useTheme,
} from '@mui/material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { ArrowBack, Close, Link } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { Connection } from '@domain/connection/models/Connection';
import { ClientType } from '@domain/connection/models/Connector';
import { APP_CONFIG } from '../../../../../config/app.config';
import { InventoryPreview } from '@features/warehouses/components/inventory-preview/InventoryPreview';
import { MutationType } from '@domain/inventory/enums/MutationType.enum';
import { RuleOperator } from '@domain/inventory/enums/RuleOperator.enum';
import { RuleField } from '@domain/inventory/enums/RuleField.enum';
import { useWarehouses } from '../../../hooks/useWarehouseOperations';
import { useToast } from '@shared/hooks/useToast';
import { WarehouseConnectionType } from '@domain/inventory/enums/WarehouseConnectionType.enum';
import Import4YouCredentialsForm from '@features/connections/import4you/components/Import4YouCredentialsForm';
import WarehouseConnectionSelect from '../WarehouseConnectionSelect';
import { useImport4YouConnector, useImport4YouProductsInventory } from '@features/connections/import4you/hooks/useImport4You';



interface Import4YouWarehouseFormProps {
    onClose: () => void;
    onBack: () => void;
}

const Import4YouWarehouseForm: React.FC<Import4YouWarehouseFormProps> = ({
    onClose,
    onBack,
}) => {


    const theme = useTheme();

    const [form, setForm] = useState({
        name: 'Import4You',
        location: '',
    });

    const queryClient = useQueryClient();
    const { error } = useToast();

    const [showCreateConnection, setShowCreateConnection] = useState(false);

    const handleCloseCreateConnection = () => {
        setShowCreateConnection(false);
    };

    const handleNewConnection = (marketplaceConnection: Connection) => {
        setShowCreateConnection(false);
        queryClient.invalidateQueries({ queryKey: ['connections'] });
        setSelectedConnection(marketplaceConnection);
    };

    const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
    const { createWarehouse, isCreating } = useWarehouses();

    const handleClose = () => {
        setForm({ name: '', location: '' });
        onClose();
    };

    const handleSubmit = async () => {
        if (!selectedConnection) {
            error('Please select a warehouse or location');
            return;
        }

        const warehouse = {
            name: form.name,
            location: form.location,
            warehouseConnections: [
                {
                    sourceType: WarehouseConnectionType.IMPORT4YOU,
                    mutationType: MutationType.SYNC,
                    rules: [
                        {
                            operator: RuleOperator.EQUALS,
                            field: RuleField.CONNECTION_ID,
                            value: selectedConnection.id
                        },
                    ],
                }
            ]
        }

        await createWarehouse(warehouse);

        handleClose();
    };

    const handleCreateConnection = () => {
        setShowCreateConnection(true);
    };

    const { connector } = useImport4YouConnector();
    const { productsInventory: mappedInventory, isLoading: isLoadingPreview } = useImport4YouProductsInventory(selectedConnection?.id);

    const [itemsToShow, setItemsToShow] = useState(10);

    const filteredInventoryPreview = mappedInventory;

    return (
        <Stack  sx={{ height: '100%' }}>
            <DialogHeader>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center">
                        <IconButton onClick={onBack} color="inherit">
                            <ArrowBack />
                        </IconButton>

                        <Typography variant="h6" component="div">
                            Sync with Import4You
                        </Typography>
                    </Stack>
                </Stack>
            </DialogHeader>

            <DialogContent sx={{ flexGrow: 1 }}>
                <Stack gap={3} sx={{ mt: 2 }}>
                    <Box>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <Box sx={{ width: 100, height: 60, borderRadius: 1, border: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border, p: 2 }}>
                                <img src={connector?.image} alt="Import4You" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" color="text.primary">
                                    Sync your inventory with Import4You
                                </Typography>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Your Import4You inventory will be synced with your Inventory every hour.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Divider />


                    <Stack gap={2} direction="column">
                        <Typography variant="body2" color="text.secondary">
                            Warehouse details
                        </Typography>

                        <Stack gap={2} direction="row">
                            <TextField
                                label="Warehouse name"
                                value={form.name}
                                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                                fullWidth
                                size="small"
                            />

                            <TextField
                                label="Location"
                                value={form.location}
                                onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                                fullWidth
                                size="small"
                                placeholder="e.g., Amsterdam, Netherlands"
                            />
                        </Stack>
                    </Stack>


                    <WarehouseConnectionSelect
                        connectorType={ClientType.IMPORT4YOU}
                        selectedConnection={selectedConnection}
                        onSelect={setSelectedConnection}
                        onCreateConnection={handleCreateConnection}
                        connectorName="Import4You"
                    />

                    {selectedConnection && (
                        <InventoryPreview
                            inventory={filteredInventoryPreview}
                            isLoading={isLoadingPreview}
                            emptyMessage="No inventory to show for this connection"
                        />
                    )}
                </Stack>
            </DialogContent >

            <DialogActions sx={{ p: 3, justifyContent: 'flex-end', borderTop: '1px solid', borderColor: theme.palette.divider }}>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!selectedConnection || isCreating}
                    startIcon={isCreating ? <CircularProgress size={20} /> : undefined}
                >
                    Create warehouse
                </Button>
            </DialogActions>

            {/* Create Import4You Connection Dialog */}
            <Dialog
                open={showCreateConnection}
                onClose={handleCloseCreateConnection}
                maxWidth="sm"
            >
                <Import4YouCredentialsForm
                    onNewConnection={handleNewConnection}
                    onClose={handleCloseCreateConnection}
                />
            </Dialog>
        </Stack>
    );
};

export default Import4YouWarehouseForm; 